import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Select, MenuItem, FormControl, InputLabel,
    Switch, FormControlLabel, Alert, Box, Grid
} from "@mui/material";

export default function AddEditFactModal({ open, onClose, onSave, editData }) {
    const { chatbotId } = useParams();
    const isEdit = !!editData;

    const [formData, setFormData] = useState({
        entity_name: '',
        attribute_name: '',
        attribute_value: '',
        start_date: '',
        good_until_date: '',
        is_recurring: false,
        recurrence_pattern: '',
        notes: '',
        status: 'Active',
        confidence_score: 1.0
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (editData) {
            setFormData({
                entity_name: editData.entity_name || '',
                attribute_name: editData.attribute_name || '',
                attribute_value: editData.attribute_value || '',
                start_date: editData.start_date ? editData.start_date.split('T')[0] : '',
                good_until_date: editData.good_until_date ? editData.good_until_date.split('T')[0] : '',
                is_recurring: editData.is_recurring || false,
                recurrence_pattern: editData.recurrence_pattern || '',
                notes: editData.notes || '',
                status: editData.status || 'Active',
                confidence_score: editData.confidence_score || 1.0
            });
        } else {
            setFormData({
                entity_name: '', attribute_name: '', attribute_value: '',
                start_date: '', good_until_date: '', is_recurring: false,
                recurrence_pattern: '', notes: '', status: 'Active', confidence_score: 1.0
            });
        }
        setError('');
    }, [editData, open]);

    const handleChange = (field) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
        if (!formData.entity_name.trim() || !formData.attribute_name.trim()) {
            setError('Entity Name and Attribute Name are required.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const payload = {
                ...formData,
                start_date: formData.start_date || null,
                good_until_date: formData.good_until_date || null,
                recurrence_pattern: formData.is_recurring ? formData.recurrence_pattern : null
            };

            if (isEdit) {
                await axiosInstance.put(`/api/facts/${editData.id}`, payload, {
                    params: { chatbot_id: chatbotId }
                });
            } else {
                await axiosInstance.post('/api/facts', payload, {
                    params: { chatbot_id: chatbotId }
                });
            }

            onSave();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{isEdit ? 'Edit Fact' : 'Add New Fact'}</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth label="Entity Name" required
                            value={formData.entity_name}
                            onChange={handleChange('entity_name')}
                            placeholder="e.g., Ramadan, Navaid, Company XYZ"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth label="Attribute Name" required
                            value={formData.attribute_name}
                            onChange={handleChange('attribute_name')}
                            placeholder="e.g., Start Date, Address, Phone"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth label="Attribute Value" multiline rows={3}
                            value={formData.attribute_value}
                            onChange={handleChange('attribute_value')}
                            placeholder="e.g., February 28, 123 Main Street"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth label="Start Date" type="date"
                            value={formData.start_date}
                            onChange={handleChange('start_date')}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth label="Good Until Date" type="date"
                            value={formData.good_until_date}
                            onChange={handleChange('good_until_date')}
                            InputLabelProps={{ shrink: true }}
                            helperText="Leave empty for indefinite validity"
                        />
                    </Grid>
                    {isEdit && (
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select value={formData.status} onChange={handleChange('status')} label="Status">
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Expired">Expired</MenuItem>
                                    <MenuItem value="Pending_Review">Pending Review</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={isEdit ? 6 : 12}>
                        <TextField
                            fullWidth label="Confidence Score" type="number"
                            inputProps={{ min: 0, max: 1, step: 0.1 }}
                            value={formData.confidence_score}
                            onChange={handleChange('confidence_score')}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Switch checked={formData.is_recurring} onChange={handleChange('is_recurring')} />}
                            label="Recurring Event"
                        />
                    </Grid>
                    {formData.is_recurring && (
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Recurrence Pattern</InputLabel>
                                <Select value={formData.recurrence_pattern} onChange={handleChange('recurrence_pattern')} label="Recurrence Pattern">
                                    <MenuItem value="daily">Daily</MenuItem>
                                    <MenuItem value="weekly">Weekly</MenuItem>
                                    <MenuItem value="monthly">Monthly</MenuItem>
                                    <MenuItem value="quarterly">Quarterly</MenuItem>
                                    <MenuItem value="annual">Annual</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth label="Notes" multiline rows={2}
                            value={formData.notes}
                            onChange={handleChange('notes')}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                    {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
